import { TEAM_MEMBER_APIS } from "libs/apis/team-members.api";
import { USER_APIS } from "libs/apis/users.api";
import { useNavigate } from "react-router-dom";
import { successToaster } from "utils/helpers/common/alert-service";
import { EventDTO } from "utils/helpers/models/common/event.dto";
import { MessageDTO } from "utils/helpers/models/shared/message.dto";
import { UserDTO } from "utils/helpers/models/users/user.dto";

const useUsers = () => {
  const navigate = useNavigate();
  const getUsers = async (
    setData: Function,
    queryParams: any = {},
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getUsers(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        users = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(users);
      setFilters((prev) => ({
        ...prev,
        page: pageNumber + 1,
        limit: pageSize,
      }));
      setTotalElements?.(totalElements);
    }
  };

  const activateUser = async (userId: number) => {
    const response = await USER_APIS.activateUser(userId);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const deactivateUser = async (userId: number) => {
    const response = await USER_APIS.deactivateUser(userId);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };

  const getUserById = async (userId: number, setData: Function) => {
    const response = await USER_APIS.getUserById(userId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getEventsById = async (
    setData: Function,
    queryParams: any = {},
    setFilters?: Function
  ) => {
    const response = await USER_APIS.getEventById(queryParams);
    const { status = false, data = null } = response || {};
    if (status) {
      const { content = [], offset = 0, count = 0 } = data || {};
      setData(content);
      setFilters((filters) => ({ ...filters, offset, count }));
    }
  };

  const getUserEventsById = async (
    setData: Function,
    queryParams: any = {},
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getEventById(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        offset = 0,
        count = 0,
        totalElements = 0,
      } = data;
      setData(content);
      if (setFilters) {
        setFilters((filters: any) => ({ ...filters, offset, count }));
      }
      if (setTotalElements) {
        setTotalElements(totalElements || count);
      }
    } else {
      setData([]);
      if (setTotalElements) {
        setTotalElements(0);
      }
    }
  };

  const getEventDetailById = async (eventId: number, setData: Function) => {
    const response = await USER_APIS.getEventDetailById(eventId);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getMinorById = async (
    queryParams: any = {},
    setData: Function,
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getMinorById(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(content);
      setFilters?.((prev: any) => ({
        ...prev,
        page: Number(pageNumber) + 1,
        size: pageSize,
      }));
      setTotalElements?.(totalElements);
    } else {
      setData([]);
      setTotalElements?.(0);
    }
  };

  const getFavoriteHostById = async (
    queryParams: any = {},
    setData: Function,
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getFavoriteHostById(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(content);
      setFilters?.((prev: any) => ({
        ...prev,
        page: Number(pageNumber) + 1,
        size: pageSize,
      }));
      setTotalElements?.(totalElements);
    } else {
      setData([]);
      setTotalElements?.(0);
    }
  };

  // this hook for get id for event wishlist
  const getEventByUserID = async (
    queryParams: any = {},
    setData: Function,
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getEventById(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        offset = 0,
        count = 0,
        totalElements = 0,
      } = data;
      setData(content);
      if (setFilters) {
        setFilters((filters: any) => ({ ...filters, offset, count }));
      }
      if (setTotalElements) {
        setTotalElements(totalElements || count);
      }
    } else {
      setData([]);
      if (setTotalElements) {
        setTotalElements(0);
      }
    }
  };

  // this  hook for get events for see more button
  const getEvents = async (
    setData: Function,
    queryParams: any = {},
    previousData: EventDTO[] = [],
    setFilters?: Function
  ) => {
    const response = await USER_APIS.getEventById(queryParams);
    const { status = false, data = {} } = response || {};
    if (status) {
      const { totalPages } = data;
      if (setFilters) setFilters((filters) => ({ ...filters, totalPages }));
      const events = data.content.map((i) => new EventDTO(i));
      setData(Array.isArray(data.content) ? [...previousData, ...events] : []);
    }
  };

  // containers/user-management/useHooks.ts

  const getCommunityMessageById = async (
    setData: React.Dispatch<React.SetStateAction<any[]>>,
    eventId: number,
    setPagination: React.Dispatch<
      React.SetStateAction<{
        count: number;
        currentPage: number;
        totalPages: number;
      }>
    >,
    page: number = 0,
    size: number = 10
  ) => {
    const { status = false, data = null } =
      await USER_APIS.getCommunityMessageById({ eventId, page, size });
    if (!status || !data) return;

    const { content = [], page: pageNumber, totalPages, totalElements } = data;

    const newMessages = content
      .map((msg: any) => new MessageDTO(msg))
      .sort(
        (a, b) =>
          new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      );

    setData((prev) => {
      const ids = new Set(prev.map((m) => m.id));
      const filtered = newMessages.filter((m) => !ids.has(m.id));

      // agar page>0, ye purani messages hongi: prepend them
      if (page > 0) {
        return [...filtered, ...prev];
      }
      // initial load ya naye messages: append
      return [...prev, ...filtered];
    });

    setPagination({
      count: totalElements,
      currentPage: pageNumber,
      totalPages,
    });
  };

  const getSubscriptionPlan = async (setData: Function) => {
    const response = await USER_APIS.getSubscriptionPlan();
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getSubscribers = async (
    setData: Function,
    queryParams: any = {},
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getSubscribers(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(content);
      setFilters((prev) => ({
        ...prev,
        page: Number(pageNumber) + 1,
        limit: pageSize,
      }));
      setTotalElements?.(totalElements);
    }
  };

  const getCogbyHostId = async (hostId: number, setData: Function) => {
    const response = await USER_APIS.getCogbyHostId(hostId);
    const { status = false, data = [] } = response || {};
    if (status) {
      setData(data);
    }
  };

  const getReviewsbyHostId = async (
    hostId,
    setData: Function,
    queryParams: any = {},
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getReviewsbyHostId(hostId, queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(content);
      setFilters((prev) => ({
        ...prev,
        page: Number(pageNumber) + 1,
        limit: pageSize,
      }));
      setTotalElements?.(totalElements);
    }
  };

  const addTimeSlots = async (id: any, body: any) => {
    const response = await USER_APIS.addTimeSlots(id, body);
    const { status = false } = response || {};
    if (status) {
      navigate(-1);
    }
  };

  // const getActivities = async (setData: Function, queryParams: any = {}) => {
  //   const response = await USER_APIS.getActivities(queryParams);
  //   const { status = false, data = null } = response || {};
  //   if (status && data) {
  //     const { content = [] } = data;
  //     setData(content);
  //   }
  // };

  const getTeamRequests = async (
    setData: Function,
    queryParams: any = {},
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getTeamRequests(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(content);
      setFilters((prev) => ({
        ...prev,
        page: Number(pageNumber) + 1,
        limit: pageSize,
      }));
      setTotalElements?.(totalElements);
    }
  };

  const approveRequest = async (userId: number, body: any) => {
    const response = await USER_APIS.approveRequest(userId, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      navigate(-1);
    }
  };

  const getUserBadges = async (
    setData: Function,
    queryParams: any = {},
    currentPage: number = 0,
    setHasMore?: Function
  ) => {
    const response = await USER_APIS.getUserBadges(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const { badges = [], pageNumber = 0, totalElements = 0, last = false } = data;
      setData((prev: any[]) => [...prev, ...badges]);

      // Use 'last' field if available, otherwise calculate
      let hasMore = false;
      if (typeof last === 'boolean') {
        hasMore = !last;
      } else {
        // Fallback calculation - ensure pageNumber is a number
        // API returns pageNumber as 1-indexed string, convert to number
        const currentPageNum = Number(pageNumber) || 0;
        // If pageNumber is 1-indexed, subtract 1 to get 0-indexed, then add 1 for next page
        // If pageNumber is 0-indexed, just add 1
        // Based on API response showing "1" for first page, it's 1-indexed
        const nextPage = currentPageNum; // Already 1-indexed, this is current page
        const loadedRecords = nextPage * (queryParams.size || 10);
        hasMore = loadedRecords < totalElements;
      }

      setHasMore?.(hasMore);
    } else {
      setHasMore?.(false);
    }
  };

  const getActivitiesEarnedPoints = async (
    setData: Function,
    queryParams: any = {},
    currentPage: number = 0,
    setHasMore?: Function
  ) => {
    const response = await USER_APIS.getActivitiesEarnedPoints(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const { content = [], pageNumber = 0, totalElements = 0, last = false } = data;
      setData((prev: any[]) => [...prev, ...content]);

      // Use 'last' field if available, otherwise calculate
      let hasMore = false;
      if (typeof last === 'boolean') {
        hasMore = !last;
      } else {
        // Fallback calculation - ensure pageNumber is a number
        const currentPageNum = Number(pageNumber) || 0;
        const nextPage = currentPageNum + 1;
        const loadedRecords = nextPage * (queryParams.size || 10);
        hasMore = loadedRecords < totalElements;
      }

      setHasMore?.(hasMore);
    } else {
      setHasMore?.(false);
    }
  };

  const getProductRequests = async (
    setProductRequests: Function,
    queryParams: any = {},
    currentPage: number = 0,
    setHasMore?: Function
  ) => {
    const response = await USER_APIS.getProductRequest(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const { content = [], pageNumber = 0, totalElements = 0, last = false } = data;
      setProductRequests((prev: any[]) => [...prev, ...content]);

      // Use 'last' field if available, otherwise calculate
      let hasMore = false;
      if (typeof last === 'boolean') {
        hasMore = !last;
      } else {
        // Fallback calculation - ensure pageNumber is a number
        const currentPageNum = Number(pageNumber) || 0;
        const nextPage = currentPageNum + 1;
        const loadedRecords = nextPage * (queryParams.size || 10);
        hasMore = loadedRecords < totalElements;
      }

      setHasMore?.(hasMore);
    } else {
      setHasMore?.(false);
    }
  };

  // const getActivities = async (setData: Function, queryParams: any = {}) => {
  //   const response = await USER_APIS.getActivities(queryParams);
  //   const { status = false, data = null } = response || {};
  //   if (status && data) {
  //     const { content = [] } = data;
  //     setData(content);
  //   }
  // };

  const getActivities = async (
    setData: Function,
    queryParams: any = {},
    currentPage: number = 0,
    setHasMore?: Function
  ) => {
    const response = await USER_APIS.getActivities(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const { content = [], pageNumber = 0, totalElements = 0 } = data;
      setData((prev: any[]) => [...prev, ...content]);

      const nextPage = pageNumber + 1;
      const loadedRecords = nextPage * (queryParams.size || 10);
      const hasMore = loadedRecords < totalElements;

      setHasMore?.(hasMore);
    }
  };

  const getAmbassadorExperiences = async (
    setData: Function,
    queryParams: any = {},
    setFilters?: Function,
    setTotalElements?: Function
  ) => {
    const response = await USER_APIS.getAmbassadorExperiences(queryParams);
    const { status = false, data = null } = response || {};
    if (status && data) {
      const {
        content = [],
        pageNumber = 0,
        pageSize = 10,
        totalElements = 0,
      } = data;
      setData(content);
      setFilters((prev) => ({
        ...prev,
        page: Number(pageNumber) + 1,
        limit: pageSize,
      }));
      setTotalElements?.(totalElements);
    }
  };

  const addAmbassadorExperience = async (body: any) => {
    const response = await USER_APIS.addAmbassadorExperience(body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
      navigate(-1);
    }
  };

  const getAmbassadorExperienceById = async (id: number, setData: Function) => {
    const response = await USER_APIS.getAmbassadorExperienceById(id);
    const { status = false, data = {} } = response || {};
    if (status) {
      setData(data);
    }
  };

  const uploadFile = async (params: FormData): Promise<any> => {
    try {
      const response = await USER_APIS.uploadFile(params);
      const { status = false, message = "", data = null } = response || {};

      if (status && data) {
        return data;
      }

      return null;
    } catch (error) {
      console.error("Upload failed", error);
      return null;
    }
  };


  const getAmbassadorCommunityMessages = async (id: number, setData: Function, params?: any) => {
    try {
      const response = await USER_APIS.getAmbassadorCommunityMessages(id, params);
      const { status = false, data = {} } = response || {};
      if (status) {
        setData(data);
      }
      return response;
    } catch (error) {
      console.error('Error fetching ambassador community messages:', error);
      return null;
    }
  };

  const postAmbassadorCommunityMessages = async (id: number, body: any) => {
    try {
      const response = await USER_APIS.postAmbassadorCommunityMessages(id, body);
      const { status = false, message = "" } = response || {};
      if (status) {
        // successToaster(message);
      }
      return response;
    } catch (error) {
      console.error('Error posting ambassador community message:', error);
      return null;
    }
  };

  const markAttendanceAmbassadorExperience = async (id: number, body: any) => {
    const response = await USER_APIS.markAttendanceAmbassadorExperience(id, body);
    const { status = false, message = "" } = response || {};
    if (status) {
      successToaster(message);
    }
  };
  
  return {
    getUsers,
    activateUser,
    deactivateUser,
    getUserById,
    getEventsById,
    getEventDetailById,
    getMinorById,
    getFavoriteHostById,
    getEventByUserID,
    getCommunityMessageById,
    getSubscriptionPlan,
    getSubscribers,
    getUserEventsById,
    getCogbyHostId,
    getReviewsbyHostId,
    getEvents,
    addTimeSlots,
    getTeamRequests,
    approveRequest,
    getUserBadges,
    getActivitiesEarnedPoints,
    getProductRequests,
    getActivities,
    getAmbassadorExperiences,
    addAmbassadorExperience,
    uploadFile,
    getAmbassadorExperienceById,
    getAmbassadorCommunityMessages,
    postAmbassadorCommunityMessages,
    markAttendanceAmbassadorExperience,
  };
};

export default useUsers;
